import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import { api } from '../documents/docApi';

export const PHOTO_ACCEPT = 'image/*,.heic,.heif';

export async function uploadPhoto(kind, id, file, main = false) {
  const fd = new FormData();
  fd.append('file', file, file.name);
  const res = await fetch(`/api/photos/${kind}/${id}${main ? '?main=1' : ''}`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Ошибка ${res.status}`);
  return data;
}

const initials = (name) =>
  String(name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

/* A person's photos: the main photo as an avatar; the gallery (add, make main,
   delete) in a dialog. kind: 'student' | 'teacher', id: person id or 'me'. */
export default function PersonPhotos({
  kind,
  id,
  name,
  canEdit = false,
  size = 96,
  onChange,
}) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [desc, setDesc] = useState('');
  const pickRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    if (!id) return;
    api(`/api/photos/${kind}/${id}`)
      .then(setData)
      .catch(() => setData({ photos: [], main: null }));
  }, [kind, id]);
  useEffect(() => {
    load();
  }, [load]);

  const changed = (v) => {
    setData(v);
    if (onChange) onChange(v);
  };
  const add = async (files) => {
    setBusy(true);
    setError('');
    try {
      let v = data;
      for (const f of files) v = await uploadPhoto(kind, id, f);
      changed(v);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const act = async (path, method, body) => {
    setError('');
    try {
      changed(await api(path, { method, body }));
    } catch (e) {
      setError(e.message);
    }
  };

  const photos = (data && data.photos) || [];
  const main = data && data.main;
  const mainDesc = main ? main.description || '' : '';
  useEffect(() => setDesc(mainDesc), [mainDesc]);
  return (
    <>
      <Tooltip
        title={
          canEdit
            ? photos.length
              ? 'Фото — открыть, добавить, выбрать главное'
              : 'Добавить фото'
            : ''
        }
      >
        <Box
          component={canEdit || photos.length ? 'button' : 'div'}
          type="button"
          onClick={() => (canEdit || photos.length) && setOpen(true)}
          sx={{
            p: 0,
            border: 0,
            background: 'none',
            cursor: canEdit || photos.length ? 'pointer' : 'default',
            position: 'relative',
            borderRadius: '50%',
            display: 'inline-flex',
          }}
          aria-label="Фото"
        >
          <Avatar
            src={main ? main.thumb : undefined}
            alt={name}
            sx={{
              width: size,
              height: size,
              fontSize: size / 3,
              fontWeight: 700,
              border: '3px solid',
              borderColor: 'background.paper',
              boxShadow: 2,
            }}
          >
            {initials(name)}
          </Avatar>
          {canEdit && !main && (
            <Box
              sx={{
                position: 'absolute',
                right: -2,
                bottom: -2,
                width: 30,
                height: 30,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: '#fff',
                display: 'grid',
                placeItems: 'center',
                boxShadow: 2,
              }}
            >
              <AddAPhotoOutlinedIcon sx={{ fontSize: 17 }} />
            </Box>
          )}
        </Box>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Фото · {name}</DialogTitle>
        <DialogContent dividers sx={{ display: 'grid', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Главное фото (★) — аватар в Core и образец лица для AI-картинок:
            наклейки, открытки, портреты. Можно добавить несколько фото — разные
            ракурсы помогают сходству. Фото видят только учитель ребёнка,
            администратор и родители.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <Box
            sx={{
              display: 'grid',
              gap: 1,
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            }}
          >
            {photos.map((p) => (
              <Box
                key={p._id}
                sx={{
                  position: 'relative',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '2px solid',
                  borderColor: p.main ? 'warning.main' : 'divider',
                }}
              >
                <Box
                  component="a"
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'block' }}
                >
                  <Box
                    component="img"
                    src={p.thumb}
                    alt=""
                    sx={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                </Box>
                {canEdit && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 4,
                      right: 4,
                      bottom: 4,
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <IconButton
                      size="small"
                      aria-label="Сделать главным"
                      onClick={() =>
                        !p.main &&
                        act(`/api/photos/${kind}/${id}/${p._id}`, 'PATCH', {
                          main: true,
                        })
                      }
                      sx={{
                        bgcolor: 'rgba(255,255,255,.9)',
                        '&:hover': { bgcolor: '#fff' },
                      }}
                    >
                      {p.main ? (
                        <StarIcon fontSize="small" color="warning" />
                      ) : (
                        <StarBorderIcon fontSize="small" />
                      )}
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label="Удалить фото"
                      onClick={() =>
                        act(`/api/photos/${kind}/${id}/${p._id}`, 'DELETE')
                      }
                      sx={{
                        bgcolor: 'rgba(255,255,255,.9)',
                        '&:hover': { bgcolor: '#fff' },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" color="error" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            ))}
            {canEdit && (
              <Box
                component="button"
                type="button"
                onClick={() => pickRef.current && pickRef.current.click()}
                disabled={busy}
                sx={{
                  aspectRatio: '1 / 1',
                  borderRadius: '12px',
                  border: '2px dashed',
                  borderColor: 'divider',
                  bgcolor: 'transparent',
                  color: 'text.secondary',
                  cursor: 'pointer',
                  display: 'grid',
                  placeItems: 'center',
                  font: 'inherit',
                }}
              >
                {busy ? (
                  <CircularProgress size={24} />
                ) : (
                  <Box sx={{ textAlign: 'center' }}>
                    <AddAPhotoOutlinedIcon />
                    <Typography variant="body2">Добавить фото</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
          {main && (canEdit || mainDesc) && (
            <TextField
              size="small"
              multiline
              minRows={1}
              maxRows={4}
              label="Как выглядит на главном фото — для наклеек (по-английски)"
              placeholder="a little girl with long dark-brown hair and a green hair clip, wearing a white top with a bunny print"
              value={desc}
              disabled={!canEdit}
              onChange={(e) => setDesc(e.target.value)}
              onBlur={() =>
                desc !== mainDesc &&
                act(`/api/photos/${kind}/${id}/${main._id}`, 'PATCH', {
                  description: desc,
                })
              }
              helperText="AI заполнит сам при первых наклейках. Поправьте, если ошибся — модель картинок читает это вместе с фото."
            />
          )}
          <input
            ref={pickRef}
            type="file"
            accept={PHOTO_ACCEPT}
            multiple
            hidden
            onChange={(e) => {
              const list = Array.from(e.target.files || []);
              e.target.value = '';
              if (list.length) add(list);
            }}
          />
        </DialogContent>
        <DialogActions>
          {main && canEdit && (
            <Button
              startIcon={<EmojiEmotionsOutlinedIcon />}
              onClick={() => {
                setOpen(false);
                navigate(
                  `/images?tab=stickers&people=${encodeURIComponent(
                    `${kind}:${id}`
                  )}`
                );
              }}
              sx={{ mr: 'auto' }}
            >
              Наклейки
            </Button>
          )}
          <Button onClick={() => setOpen(false)}>Готово</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
